/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { z } from 'zod'
import { withValidate } from 'lib/zod-validate'
import {isStrictIsoDate} from 'utils/formValidation'

const positiveId = (message: string) => z.number({message}).int(message).positive(message)

export const OfferBasicSchema = withValidate(
    z.preprocess(
        (input) => {
            if (input && typeof input === 'object') {
                const asAny = input as Record<string, unknown>
                // Support both `children` and legacy `childrens` field names
                if (asAny.childrens !== undefined && asAny.children === undefined) {
                    asAny.children = asAny.childrens
                }
            }
            return input
        },
        z.object({
            name: z.string().trim().optional().nullable(),
            date: z.string().trim().min(1, 'Date is required').refine(isStrictIsoDate, 'Enter a valid event date'),
            valid_until: z.string().trim().min(1, 'Valid until is required').refine(isStrictIsoDate, 'Enter a valid validity date'),
            adults: z
                .number({
                    message: 'Adults must be a number',
                })
                .int('Adults must be a whole number').min(0, 'Adults must not be negative').max(500, 'Adults must not exceed 500'),
            children: z
                .number({
                    message: 'Children must be a number',
                })
                .int('Children must be a whole number').min(0, 'Children must not be negative').max(500, 'Children must not exceed 500'),
            company_id: positiveId('Company is required'),
            contact_id: positiveId('Contact must be a valid ID').nullable().optional(),
            probability: z
                .number({
                    message: 'Probability must be a number',
                })
                .min(0, 'Probability must be between 0 and 100')
                .max(100, 'Probability must be between 0 and 100')
                .nullable()
                .optional(),
            duration_minutes: z
                .number({
                    message: 'Duration must be a number',
                })
                .min(0)
                .nullable()
                .optional(),
        }).superRefine((data, ctx) => {
            if (data.adults + data.children < 1) ctx.addIssue({code: 'custom', path: ['adults'], message: 'Select at least one person'})
            if (isStrictIsoDate(data.date) && isStrictIsoDate(data.valid_until) && data.valid_until > data.date) {
                ctx.addIssue({code: 'custom', path: ['valid_until'], message: 'Valid until must not be after the event date'})
            }
        })
    )
)

export type OfferBasicInputs = z.infer<typeof OfferBasicSchema>
